import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getApiUrl } from "../../../api/axiosClient/utils";
import { loggedInApiCall } from "../../../api/axiosClient/userApi";
import { useGetEnabledLocales } from "../../../api/locales/useLocales";
import { FoodsIcon } from "@foodpilot/foods";

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Pagination,
    TableFooter,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import FilterIcon from "@mui/icons-material/FilterList";
import OverrideIcon from "@mui/icons-material/PersonalVideo";

interface Translation {
    id?: number;
    locale: string;
    key: string;
    value: string;
    isOverride: boolean;
    updatedAt?: string;
}

export const TranslationManagement = () => {
    const { t } = useTranslation();

    // Fetch enabled locales from API
    const { data: enabledLocales, isLoading: loadingLocales, error: localesError } = useGetEnabledLocales();

    const [selectedLocale, setSelectedLocale] = useState("en"); // Simple default
    const [translations, setTranslations] = useState<Translation[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Dialog states
    const [editDialog, setEditDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [editingTranslation, setEditingTranslation] = useState<Translation | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showOnlyOverrides, setShowOnlyOverrides] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(30);

    const apiUrl = getApiUrl();

    // Load translations for current locale
    const loadTranslations = async (locale: string, page: number = 1) => {
        setLoading(true);
        setError(null);

        try {
            if (showOnlyOverrides) {
                // Load only overrides with server-side pagination
                const params = new URLSearchParams({
                    locale,
                    isOverride: "true",
                    page: page.toString(),
                    itemsPerPage: itemsPerPage.toString(),
                });

                if (searchTerm) {
                    params.append("translationKey", searchTerm);
                }

                const response = await loggedInApiCall.get(`/api/interface_translations?${params.toString()}`);
                const data = response?.data || { "hydra:member": [], "hydra:totalItems": 0 };

                const translationArray: Translation[] = data["hydra:member"].map(
                    (item: {
                        id: number;
                        locale: string;
                        translationKey: string;
                        value: string;
                        isOverride: boolean;
                        updatedAt: string;
                    }) => ({
                        id: item.id,
                        locale: item.locale,
                        key: item.translationKey,
                        value: item.value,
                        isOverride: item.isOverride,
                        updatedAt: item.updatedAt,
                    }),
                );

                setTranslations(translationArray);
                setTotalItems(data["hydra:totalItems"] || 0);
            } else {
                // Load all translations for the locale (merged with overrides)
                const response = await fetch(`${apiUrl}/api/translations/${locale}`);
                if (!response.ok)
                    throw new Error(t("translations.management.load_error", "Failed to load translations"));

                const data = await response.json();

                // Load override entities to get IDs
                const overridesResponse = await loggedInApiCall.get(`/api/interface_translations?locale=${locale}`);
                const overridesData = overridesResponse?.data || { "hydra:member": [] };

                // Create a map of overrides by key
                const overridesMap = new Map<string, { id: number; isOverride: boolean; updatedAt: string }>();
                if (overridesData["hydra:member"]) {
                    overridesData["hydra:member"].forEach(
                        (override: { id: number; translationKey: string; isOverride: boolean; updatedAt: string }) => {
                            overridesMap.set(override.translationKey, {
                                id: override.id,
                                isOverride: override.isOverride,
                                updatedAt: override.updatedAt,
                            });
                        },
                    );
                }

                // Convert flat object to array with override info
                const translationArray: Translation[] = [];
                for (const [key, value] of Object.entries(data)) {
                    const overrideInfo = overridesMap.get(key);

                    translationArray.push({
                        id: overrideInfo?.id,
                        locale,
                        key,
                        value: value as string,
                        isOverride: overrideInfo?.isOverride || false,
                        updatedAt: overrideInfo?.updatedAt,
                    });
                }

                setTranslations(translationArray);
                setTotalItems(translationArray.length);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error");
        } finally {
            setLoading(false);
        }
    };

    // Save or update translation override
    const saveTranslation = async (translation: Translation) => {
        setLoading(true);
        setError(null);

        try {
            const isUpdate = !!translation.id;
            const url = isUpdate ? `/api/interface_translations/${translation.id}` : `/api/interface_translations`;

            const body =
                isUpdate ?
                    { value: translation.value }
                :   {
                        locale: translation.locale,
                        translationKey: translation.key,
                        value: translation.value,
                        isOverride: true,
                    };

            if (isUpdate) {
                await loggedInApiCall.put(url, body);
            } else {
                await loggedInApiCall.post(url, body);
            }

            setSuccess(t("translations.management.save_success", "Translation saved successfully"));
            setEditDialog(false);
            setEditingTranslation(null);
            await loadTranslations(selectedLocale, currentPage);
        } catch (err) {
            setError(err instanceof Error ? err.message : t("common.unknown_error", "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    // Delete translation override
    const deleteTranslation = async () => {
        if (!editingTranslation || !editingTranslation.id) return;

        setLoading(true);
        setError(null);

        try {
            await loggedInApiCall.delete(`/api/interface_translations/${editingTranslation.id}`);

            setSuccess(t("translations.management.delete_success", "Translation override removed successfully"));
            setDeleteDialog(false);
            setEditingTranslation(null);
            await loadTranslations(selectedLocale, currentPage);
        } catch (err) {
            setError(err instanceof Error ? err.message : t("common.unknown_error", "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    // Sync with Sure API (clears cache + resyncs)
    const syncWithSureApi = async () => {
        setLoading(true);
        setError(null);

        try {
            // First clear the cache
            await loggedInApiCall.post("/api/translations/cache/clear", { locale: selectedLocale });

            // Then sync with Sure API
            await loggedInApiCall.post("/api/translations/sync", { locale: selectedLocale });

            setSuccess(
                t("translations.management.sync_success", "Cache cleared and synchronized with Sure API successfully"),
            );
            await loadTranslations(selectedLocale, currentPage);
        } catch (err) {
            setError(err instanceof Error ? err.message : t("common.unknown_error", "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    // Filter translations based on search term (only when not showing overrides)
    const filteredTranslations =
        showOnlyOverrides ? translations : (
            translations.filter((translation) => {
                const matchesSearch =
                    translation.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    translation.value.toLowerCase().includes(searchTerm.toLowerCase());

                return matchesSearch;
            })
        );

    // Count overrides for display
    const overrideCount = translations.filter((t) => t.isOverride).length;
    const totalCount = translations.length;

    // Calculate pagination values
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [showOnlyOverrides, searchTerm, itemsPerPage]);

    // Load translations when dependencies change
    useEffect(() => {
        loadTranslations(selectedLocale, currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocale, currentPage, itemsPerPage, showOnlyOverrides, searchTerm]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 10000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Set selectedLocale to first available locale when locales are loaded
    useEffect(() => {
        if (enabledLocales && enabledLocales.length > 0 && !selectedLocale) {
            setSelectedLocale(enabledLocales[0].code || "en");
        }
    }, [enabledLocales, selectedLocale]);

    // Set error state from API
    useEffect(() => {
        if (localesError) {
            setError("Failed to load enabled locales from API");
        }
    }, [localesError]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {t("translations.management.title", "Translations")}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                    "translations.management.description",
                    "Manage application translations and create local overrides.",
                )}
            </Typography>

            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {t("translations.management.stats", "{{overrides}} overrides / {{total}} total translations", {
                        overrides: overrideCount,
                        total: totalCount,
                    })}
                </Typography>
                <Button
                    variant={showOnlyOverrides ? "contained" : "outlined"}
                    size="small"
                    startIcon={<FilterIcon />}
                    onClick={() => setShowOnlyOverrides(!showOnlyOverrides)}
                    color={showOnlyOverrides ? "primary" : "inherit"}
                >
                    {showOnlyOverrides ?
                        t("translations.management.show_all", "Show All")
                    :   t("translations.management.show_overrides_only", "Show Overrides Only")}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {loadingLocales && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={16} />
                        {t("translations.management.loadingLocales", "Loading available languages...")}
                    </Box>
                </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>{t("translations.management.locale", "Locale")}</InputLabel>
                        <Select
                            value={selectedLocale}
                            label={t("translations.management.locale", "Locale")}
                            onChange={(e) => setSelectedLocale(e.target.value)}
                            disabled={loadingLocales}
                        >
                            {enabledLocales
                                ?.filter((locale) => locale.code)
                                .map((locale) => (
                                    <MenuItem key={locale.code!} value={locale.code!}>
                                        {locale.nativeName || locale.name} ({locale.code})
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label={t("translations.management.search", "Search translations")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={5}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon size={2} icon="plus" />}
                            onClick={() => {
                                setEditingTranslation({
                                    locale: selectedLocale,
                                    key: "",
                                    value: "",
                                    isOverride: true,
                                });
                                setEditDialog(true);
                            }}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    color: "white !important",
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    opacity: 0.36,
                                    color: "white !important",
                                },
                            })}
                        >
                            {t("translations.management.add", "Add Override")}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<SyncIcon />}
                            onClick={syncWithSureApi}
                            disabled={loading}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                border: `1px solid ${theme.palette.primary[600]}`,
                                color: `${theme.palette.primary[600]} !important`,
                                backgroundColor: "transparent",
                                "&:hover": {
                                    border: `1px solid ${theme.palette.primary[700]}`,
                                    color: `${theme.palette.primary[700]} !important`,
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                                "&:disabled": {
                                    border: `1px solid ${theme.palette.primary[600]}`,
                                    color: `${theme.palette.primary[600]} !important`,
                                    opacity: 0.36,
                                },
                            })}
                        >
                            {t("translations.management.sync", "Sync with Sure API")}
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <TableContainer component={Paper} elevation={0}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("translations.management.key", "Key")}</TableCell>
                            <TableCell>{t("translations.management.value", "Value")}</TableCell>
                            <TableCell>{t("translations.management.type", "Type")}</TableCell>
                            <TableCell>{t("translations.management.actions", "Actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ?
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        :   filteredTranslations.map((translation) => (
                                <TableRow key={translation.key}>
                                    <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {translation.isOverride && (
                                                <OverrideIcon
                                                    sx={{ fontSize: 16, color: "primary.main" }}
                                                    titleAccess="Override translation"
                                                />
                                            )}
                                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                                {translation.key}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}
                                        >
                                            {translation.value}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={translation.isOverride ? "Override" : "Global"}
                                            color={translation.isOverride ? "primary" : "default"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => {
                                                setEditingTranslation(translation);
                                                setEditDialog(true);
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        {translation.isOverride && (
                                            <IconButton
                                                onClick={() => {
                                                    setEditingTranslation(translation);
                                                    setDeleteDialog(true);
                                                }}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                    {showOnlyOverrides && totalPages > 1 && (
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            py: 2,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t(
                                                    "translations.management.showing_items",
                                                    "Showing {{count}} of {{total}} overrides",
                                                    {
                                                        count: translations.length,
                                                        total: totalItems,
                                                    },
                                                )}
                                            </Typography>
                                            <FormControl
                                                size="small"
                                                sx={{
                                                    minWidth: 120,
                                                    "& .MuiOutlinedInput-root": {
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: (theme) => theme.palette.primary[600],
                                                        },
                                                    },
                                                }}
                                            >
                                                <Select
                                                    value={itemsPerPage}
                                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                                >
                                                    <MenuItem value={10}>
                                                        {t("translations.management.per_page", "{{count}} per page", {
                                                            count: 10,
                                                        })}
                                                    </MenuItem>
                                                    <MenuItem value={30}>
                                                        {t("translations.management.per_page", "{{count}} per page", {
                                                            count: 30,
                                                        })}
                                                    </MenuItem>
                                                    <MenuItem value={50}>
                                                        {t("translations.management.per_page", "{{count}} per page", {
                                                            count: 50,
                                                        })}
                                                    </MenuItem>
                                                    <MenuItem value={100}>
                                                        {t("translations.management.per_page", "{{count}} per page", {
                                                            count: 100,
                                                        })}
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage}
                                            onChange={(_, page) => setCurrentPage(page)}
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                "& .MuiPaginationItem-root": {
                                                    color: "#393A36",
                                                },
                                                "& .MuiPaginationItem-root.Mui-selected": {
                                                    backgroundColor: (theme) => theme.palette.primary[600],
                                                    color: "white",
                                                    "&:hover": {
                                                        backgroundColor: (theme) => theme.palette.primary[700],
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingTranslation?.id ? "Edit Translation" : "Add Translation Override"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Translation Key"
                        value={editingTranslation?.key || ""}
                        onChange={(e) =>
                            setEditingTranslation((prev) => (prev ? { ...prev, key: e.target.value } : null))
                        }
                        margin="normal"
                        disabled={!!editingTranslation?.id}
                        helperText="Use dot notation (e.g., product.create.button)"
                    />
                    <TextField
                        fullWidth
                        label="Translation Value"
                        value={editingTranslation?.value || ""}
                        onChange={(e) =>
                            setEditingTranslation((prev) => (prev ? { ...prev, value: e.target.value } : null))
                        }
                        margin="normal"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>{t("common.actions.cancel", "Annuler")}</Button>
                    <Button
                        onClick={() => editingTranslation && saveTranslation(editingTranslation)}
                        variant="contained"
                        disabled={!editingTranslation?.key || !editingTranslation?.value || loading}
                    >
                        {loading ?
                            <CircularProgress size={20} />
                        :   t("common.actions.save", "Enregistrer")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
                <DialogTitle>{t("translations.management.confirmDelete", "Confirmer la suppression")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t(
                            "translations.management.confirmDeleteDescription",
                            "Are you sure you want to delete this translation override? The global translation will be used",
                        )}
                        instead.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={deleteTranslation} color="error" disabled={loading}>
                        {loading ?
                            <CircularProgress size={20} />
                        :   "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
